import React, { useCallback, useEffect, useState, useTransition } from 'react'
import { Logger } from '@/lib/logger'

const logger = new Logger('Profile')

import { useNavigate } from 'react-router-dom'

import { Formik, Form, Field, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { Stack, Avatar, Typography, Alert, Button, Divider, IconButton, CircularProgress, Switch } from '@mui/material'
import {
  Edit,
  BadgeOutlined,
  PermIdentityOutlined,
  EmailOutlined,
  LogoutOutlined,
  LockOutlined,
  CancelOutlined,
  RestartAltOutlined,
  PhoneOutlined,
  LockPerson,
} from '@mui/icons-material'

import { useAuth } from '@/hooks/useAuth'
import { useBiometric } from '@/hooks/useBiometric'
import { useToast } from '@/contexts/ToastContext'

import { biometricUtils } from '@/utils/biometric'

import { AppInfo } from '@/components/AppInfo'
import { TextInput } from '@/components/Inputs/Text'
import { MaskedInput } from '@/components/Inputs/Masked'
import PageHeader from '@/components/PageHeader'
import DeleteAccountModal from '@/components/Modals/DeleteAccountModal'
import ConfirmSaveModal from '@/components/Modals/ConfirmSaveModal'

import { HttpError } from '@/api/http'
import { useQueryAuthVerifyEmailRequest } from '@/api/auth/queries'
import { useQueryGetLockoutStatus } from '@/api/users/queries'

import { useBackButton } from '@/hooks/useBackButton'

import { AppRoutes } from '@/routes/types/routes'
import { NewPasswordInput } from '@/components/Inputs/NewPassword'
import { PasswordInput } from '@/components/Inputs/Password'
import { validatePassword } from '@/components/Inputs/NewPassword/helpers/validatePassword'

export interface ProfileFormData {
  first_name: string
  last_name: string
  cpf: string
  phone: string
  email: string
}

export interface PasswordFormData {
  current_password: string
  new_password: string
}

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required('O nome é obrigatório'),
  last_name: Yup.string().required('O sobrenome é obrigatório'),
  cpf: Yup.string()
    .test('is-valid-cpf', 'O CPF informado é inválido', (value) => {
      if (!value) return false
      return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value)
    })
    .required('O CPF é obrigatório'),
  email: Yup.string().email('O e-mail informado é inválido').required('O e-mail é obrigatório'),
})

const passwordValidationSchema = Yup.object().shape({
  current_password: Yup.string().required('A senha atual é obrigatória'),
  new_password: Yup.string().required('A nova senha é obrigatória').test({
    name: 'password',
    test: validatePassword,
    message: '',
  }),
})

const ProfilePage: React.FC = () => {
  useBackButton({ backRoute: AppRoutes.INDEX })

  const toast = useToast()
  const navigate = useNavigate()

  const { user, updateInfo, updatePassword, deleteAccount, logout, isLoading: isAuthLoading } = useAuth()
  const biometric = useBiometric()

  const [biometricEnabled, setBiometricEnabled] = useState(false)
  const [isBiometricTogglePending, startBiometricToggleTransition] = useTransition()

  useEffect(() => {
    biometricUtils.isEnabled().then(setBiometricEnabled)
  }, [])

  const handleToggleBiometric = () => {
    startBiometricToggleTransition(async () => {
      try {
        if (!biometricEnabled) {
          await biometric.authenticate(`Confirme sua identidade para ativar o login biométrico`)
          await biometricUtils.setEnabled(true)
          setBiometricEnabled(true)
          toast.success(`Login biométrico ativado com sucesso!`)
        } else {
          await biometricUtils.setEnabled(false)
          setBiometricEnabled(false)
          toast.success('Login biométrico desativado')
        }
      } catch (error) {
        if (!biometric.isUserCancel(error)) {
          toast.error('Não foi possível alterar a configuração de biometria')
        }
      }
    })
  }

  const [isEditing, setIsEditing] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openConfirmSaveModal, setOpenConfirmSaveModal] = useState(false)
  const [pendingValues, setPendingValues] = useState<ProfileFormData | null>(null)

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isEditingPassword, setIsEditingPassword] = useState(false)

  const {
    refetch: refetchVerifyEmailRequest,
    isLoading: isVerifyEmailRequestLoading,
    isFetching: isVerifyEmailRequestFetching,
    isRefetching: isVerifyEmailRequestRefetching,
  } = useQueryAuthVerifyEmailRequest({ enabled: false })

  const NEXT_ATTEMPT_INTERVAL = 60 * 1000;

  const { data: lockoutStatus, refetch: refetchLockoutStatus } = useQueryGetLockoutStatus({
    refetchInterval: (query) => query.state.data?.is_locked ? NEXT_ATTEMPT_INTERVAL : false,
  })


  const userRegisterYear = user?.created_at ? new Date(user.created_at).getFullYear() : null

  const initialValues: ProfileFormData = {
    first_name: user?.first_name ?? '',
    last_name: user?.last_name ?? '',
    cpf: user?.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') ?? '',
    phone: user?.phone.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '$1 ($2) $3-$4') ?? '',
    email: user?.email ?? '',
  }

  const handleResendVerifyEmail = async () => {
    refetchVerifyEmailRequest()
      .then(() => {
        toast.show('E-mail de verificação reenviado com sucesso!', { severity: 'success' })
      })
      .catch(() => {
        toast.show('Ocorreu um erro ao tentar reenviar o e-mail de verificação!', { severity: 'error' })
      })
  }

  const handleOnClickLogout = () => {
    logout()
      .then(() => {
        toast.show('Você saiu da sua conta com sucesso!', { severity: 'success' })
        navigate('/auth')
      })
      .catch(() => {
        toast.show('Ocorreu um erro ao tentar sair da conta!', { severity: 'error' })
      })
  }

  const handleToggleEdit = () => {
    setIsEditing((prev) => !prev)
  }

  const handleToggleEditPassword = () => {
    setIsEditingPassword((prev) => !prev)
  }

  const handleUserChange = async (values: ProfileFormData, { setSubmitting }: FormikHelpers<ProfileFormData>) => {
    if (!user) {
      toast.show('Usuário não encontrado.', { severity: 'error' })
      setSubmitting(false)
      return
    }

    if (values.email !== (user.email ?? '')) {
      setPendingValues(values)
      setOpenConfirmSaveModal(true)
      setSubmitting(false)
      return
    }

    setSubmitError(null)

    try {
      await updateInfo({
        first_name: values.first_name,
        last_name: values.last_name,
        cpf: values.cpf,
        phone: values.phone,
        email: values.email,
      })

      toast.success('Perfil atualizado com sucesso!')

      setIsEditing(false)
    } catch (error) {
      setSubmitError((error as HttpError).message || 'Ocorreu um erro ao atualizar o perfil.')
      toast.error((error as HttpError).message || 'Ocorreu um erro ao atualizar o perfil.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirmSave = async (password: string) => {
    if (!pendingValues) return

    setSubmitError(null)

    // Throws on error so ConfirmSaveModal can display it
    await updateInfo({
      first_name: pendingValues.first_name,
      last_name: pendingValues.last_name,
      cpf: pendingValues.cpf,
      phone: pendingValues.phone,
      email: pendingValues.email,
      old_password: password,
      redirect_path: AppRoutes.PROFILE,
    })

    toast.info('Enviamos um e-mail de confirmação para o novo endereço. Acesse para concluir a alteração.', {
      duration: 8000,
    })
    toast.success('Perfil atualizado com sucesso!')

    setOpenConfirmSaveModal(false)
    setPendingValues(null)
    setIsEditing(false)
  }

  const handlePasswordChange = async (values: PasswordFormData, { setSubmitting }: FormikHelpers<PasswordFormData>) => {
    if (!user) {
      toast.show('Usuário não encontrado.', { severity: 'error' })

      setSubmitting(false)
      return
    }

    setSubmitError(null)
    try {
      await updatePassword(values.current_password, values.new_password)

      toast.show('Senha alterada com sucesso!', { severity: 'success' })

      setIsEditingPassword(false)
    } catch (error) {
      setSubmitError((error as HttpError).message || 'Ocorreu um erro ao alterar a senha.')
      toast.error((error as HttpError).message || 'Ocorreu um erro ao alterar a senha.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteAccount = useCallback(async () => {
    deleteAccount()
      .then(() => {
        toast.show('Sua conta foi excluída com sucesso!', { severity: 'success' })

        navigate('/auth')
      })
      .catch((e) => {
        logger.error('Erro ao excluir conta', e)
        toast.show('Ocorreu um erro ao tentar excluir a conta!', { severity: 'error' })
      })
  }, [deleteAccount])

  return (
    <Stack paddingTop="24px" paddingX="24px">
      <Stack
        direction="column"
        spacing={2}
        sx={{
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <PageHeader
          endComponent={
            isAuthLoading ? (
              <CircularProgress color="error" size={24} sx={{ position: 'absolute', right: 0 }} />
            ) : (
              <IconButton color="error" onClick={handleOnClickLogout} sx={{ position: 'absolute', right: 0 }}>
                <LogoutOutlined />
              </IconButton>
            )
          }
        />

        <Stack
          direction="column"
          spacing={2}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Avatar
            alt={user?.first_name || 'Usuário'}
            sx={{
              width: '128px',
              height: '128px',
            }}
          />
          <Stack direction="column" alignItems="center">
            <Typography variant="h5">{user?.first_name}</Typography>
            {userRegisterYear && <Typography variant="body1">Usuário desde {userRegisterYear}</Typography>}
          </Stack>
        </Stack>

        {user?.email_verified === false ? (
          <Alert
            severity="warning"
            action={
              isVerifyEmailRequestLoading || isVerifyEmailRequestFetching || isVerifyEmailRequestRefetching ? (
                <CircularProgress color="warning" size={40} sx={{ padding: 1 }} />
              ) : (
                <IconButton color="warning" onClick={handleResendVerifyEmail}>
                  <RestartAltOutlined />
                </IconButton>
              )
            }
            sx={{ borderRadius: 4, alignItems: 'center' }}
          >
            <Typography variant="body2" fontSize="0.8rem" fontWeight="bold">
              Confirmação de e-mail pendente
            </Typography>
          </Alert>
        ) : null}

        {user?.email_to_change ? (
          <Alert severity="info" sx={{ borderRadius: 4, alignItems: 'center' }}>
            <Typography variant="body2" fontSize="0.8rem" fontWeight="bold">
              Troca de e-mail pendente
            </Typography>
            <Typography variant="body2" fontSize="0.75rem" color="text.secondary">
              Confirme no novo endereço: {user.email_to_change}
            </Typography>
          </Alert>
        ) : null}

        {lockoutStatus?.is_locked && lockoutStatus.remaining_locked_time && (
          <Alert severity="warning" sx={{ borderRadius: 4, alignItems: 'center'}}>
            <Typography variant="body2" fontSize="0.8rem" fontWeight="bold">
              Alteração de e-mail bloqueada por tentativas incorretas. Tente novamente em {lockoutStatus.remaining_locked_time}.
            </Typography>
          </Alert>
        )}

        {isEditing ? (
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleUserChange}>
            {({ resetForm, values }) => (
              <Form>
                <Stack direction="row" alignItems="center" justifyContent="space-between" marginBottom={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Informações
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      color="error"
                      variant="text"
                      startIcon={<CancelOutlined />}
                      onClick={() => {
                        resetForm()
                        setIsEditing(false)
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button variant="text" startIcon={<Edit />} type="submit"
                      disabled={lockoutStatus?.is_locked && values.email !== (user?.email ?? '')}>
                      Salvar
                    </Button>
                  </Stack>
                </Stack>

                {submitError ? (
                  <Alert
                    severity="error"
                    sx={{ width: '100%', alignItems: 'center', borderRadius: 4, marginBottom: 2 }}
                  >
                    {submitError}
                  </Alert>
                ) : null}

                <Stack direction="column" bgcolor="white" borderRadius={4}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingY={1.5}
                    paddingX={2}
                    spacing={1}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <PermIdentityOutlined fontSize="small" />
                    </Stack>
                    <Field
                      component={TextInput}
                      size="normal"
                      name="first_name"
                      placeholder="Nome"
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  <Divider />
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingY={1.5}
                    paddingX={2}
                    spacing={1}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <PermIdentityOutlined fontSize="small" />
                    </Stack>
                    <Field
                      component={TextInput}
                      size="normal"
                      name="last_name"
                      placeholder="Sobrenome"
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  <Divider />
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingY={1.5}
                    paddingX={2}
                    spacing={1}
                    sx={{ cursor: 'not-allowed' }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ opacity: 0.38 }}>
                      <BadgeOutlined fontSize="small" />
                    </Stack>
                    <Field
                      component={TextInput}
                      size="normal"
                      name="cpf"
                      placeholder="Digite seu CPF"
                      variant="standard"
                      disabled
                      InputProps={{
                        inputComponent: MaskedInput,
                        inputProps: {
                          opacity: 0.5,
                        },
                        disableUnderline: true,
                      }}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  <Divider />
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingY={1.5}
                    paddingX={2}
                    spacing={1}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <PhoneOutlined fontSize="small" />
                    </Stack>
                    <Field
                      component={TextInput}
                      size="normal"
                      placeholder="Telefone"
                      variant="standard"
                      name="phone"
                      autoComplete="tel"
                      InputProps={{
                        inputComponent: MaskedInput,
                        inputProps: {
                          inputMode: 'numeric',
                          mask: '+55 (__) _____-____',
                          replacement: {
                            _: /[0-9]/,
                          },
                        },
                        disableUnderline: true,
                      }}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  <Divider />
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingY={1.5}
                    paddingX={2}
                    spacing={1}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <EmailOutlined fontSize="small" />
                    </Stack>
                    <Field
                      component={TextInput}
                      size="normal"
                      name="email"
                      placeholder="E-mail"
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        ) : (
          <Stack direction="column" spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight="bold">
                Informações
              </Typography>
              <Button variant="text" startIcon={<Edit />} onClick={handleToggleEdit}>
                Editar
              </Button>
            </Stack>
            <Stack direction="column" bgcolor="white" borderRadius={4}>
              <Stack direction="row" alignItems="center" padding={2} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={0.5} minWidth={100} width={100}>
                  <PermIdentityOutlined fontSize="small" />
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    Nome
                  </Typography>
                </Stack>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body1" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                  {user?.first_name || 'Sem nome cadastrado'}
                </Typography>
              </Stack>
              <Divider />
              <Stack direction="row" alignItems="center" padding={2} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={0.5} minWidth={100} width={100}>
                  <PermIdentityOutlined fontSize="small" />
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    Sobrenome
                  </Typography>
                </Stack>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body1" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                  {user?.last_name || 'Sem sobrenome cadastrado'}
                </Typography>
              </Stack>
              <Divider />
              <Stack direction="row" alignItems="center" padding={2} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={0.5} minWidth={100} width={100}>
                  <BadgeOutlined fontSize="small" />
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    CPF
                  </Typography>
                </Stack>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body1" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                  {user?.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') || 'Sem CPF cadastrado'}
                </Typography>
              </Stack>
              <Divider />
              <Stack direction="row" alignItems="center" padding={2} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={0.5} minWidth={100} width={100}>
                  <PhoneOutlined fontSize="small" />
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    Telefone
                  </Typography>
                </Stack>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body1" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                  {user?.phone.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '$1 ($2) $3-$4') || 'Sem telefone cadastrado'}
                </Typography>
              </Stack>
              <Divider />
              <Stack direction="row" alignItems="center" padding={2} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={0.5} minWidth={100} width={100}>
                  <EmailOutlined fontSize="small" />
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    E-mail
                  </Typography>
                </Stack>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body1" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                  {user?.email || 'Sem e-mail cadastrado'}
                </Typography>
              </Stack>
              {user?.email_to_change ? (
                <React.Fragment>
                  <Divider />
                  <Stack direction="row" alignItems="center" padding={2} spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={0.5} minWidth={100} width={100}>
                      <EmailOutlined fontSize="small" />
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        overflow="hidden"
                      >
                        Novo e-mail
                      </Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="body1" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                      {user?.email_to_change}
                    </Typography>
                  </Stack>
                </React.Fragment>
              ) : null}
            </Stack>
          </Stack>
        )}

        {isEditingPassword ? (
          <Formik
            initialValues={{ current_password: '', new_password: '' }}
            validationSchema={passwordValidationSchema}
            onSubmit={handlePasswordChange}
          >
            {({ resetForm }) => (
              <Form>
                <Stack direction="row" alignItems="center" justifyContent="space-between" marginBottom={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Alterar senha
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      color="error"
                      variant="text"
                      startIcon={<CancelOutlined />}
                      onClick={() => {
                        resetForm()
                        setIsEditingPassword(false)
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button variant="text" startIcon={<Edit />} type="submit">
                      Salvar
                    </Button>
                  </Stack>
                </Stack>

                {submitError ? (
                  <Alert
                    severity="error"
                    sx={{ width: '100%', alignItems: 'center', borderRadius: 4, marginBottom: 2 }}
                  >
                    {submitError}
                  </Alert>
                ) : null}

                <Stack direction="column" bgcolor="white" borderRadius={4}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingY={1.5}
                    paddingX={2}
                    spacing={1}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <LockOutlined fontSize="small" />
                    </Stack>

                    <PasswordInput name="current_password" placeholder="Senha atual" variant="standard" size="normal" />
                  </Stack>
                  <Divider />
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingY={1.5}
                    paddingX={2}
                    spacing={1}
                  >
                    <Stack direction="row" alignItems="center" alignSelf="flex-start" pt={0.5} spacing={0.5}>
                      <LockOutlined fontSize="small" />
                    </Stack>
                    <NewPasswordInput name="new_password" size="normal" variant="standard" placeholder="Nova senha" />
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        ) : (
          <Stack direction="column" spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight="bold">
                Alterar senha
              </Typography>
              <Button variant="text" startIcon={<Edit />} onClick={handleToggleEditPassword}>
                Editar
              </Button>
            </Stack>
            <Stack direction="column" bgcolor="white" borderRadius={4}>
              <Stack direction="row" alignItems="center" padding={2} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={0.5} minWidth={100} width={100}>
                  <LockOutlined fontSize="small" />
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    Senha
                  </Typography>
                </Stack>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body1" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                  ••••••••••
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
        <Stack direction="column" spacing={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            Segurança
          </Typography>
          <Stack direction="column" bgcolor="white" borderRadius={4} sx={{ opacity: biometric.available ? 1 : 0.5 }}>
            <Stack direction="row" alignItems="center" padding={2} spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1} flex={1}>
                <LockPerson fontSize="medium" />
                <Stack direction="column">
                  <Typography variant="body2" fontWeight="bold">
                    Login com biometria
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {biometric.isChecking
                      ? 'Verificando disponibilidade...'
                      : biometric.available
                        ? 'Entrar sem digitar a senha'
                        : 'Indisponível neste dispositivo'}
                  </Typography>
                </Stack>
              </Stack>
              <Switch
                checked={biometricEnabled && biometric.available}
                onChange={handleToggleBiometric}
                disabled={!biometric.available || biometric.isChecking || isBiometricTogglePending}
              />
            </Stack>
          </Stack>
        </Stack>

        <Divider />
        <Button variant="contained" color="error" onClick={() => setOpenDeleteModal(true)} sx={{ borderRadius: 20 }}>
          Excluir minha conta
        </Button>
        <AppInfo />

        <DeleteAccountModal
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          onDelete={handleDeleteAccount}
        />

        <ConfirmSaveModal
          open={openConfirmSaveModal}
          onClose={() => {
            setOpenConfirmSaveModal(false)
            setPendingValues(null)
          }}
          onConfirm={handleConfirmSave}
          lockoutStatus={lockoutStatus}
          refetchLockoutStatus={refetchLockoutStatus}
        />
      </Stack>
    </Stack>
  )
}

export default ProfilePage
